// CurrentDocumentDownload — the shared "download the document in its CURRENT
// state" control for staff DETAIL views (drawers / modals / detail routes —
// never list rows).
//
// Wraps GET /api/lease-drafts/:groupId/current-document: executed → the signed
// PDF; mid-signing → the live DocuSeal combined PDF with every mark so far
// (WEL-29); generated-but-never-sent lease → the newest generated version.
//
// On mount it runs the cheap ?probe=1 availability check (no DocuSeal
// round-trip server-side). When no document exists yet it says so plainly
// instead of rendering a button that can only fail; a click that 404s
// degrades to the same note.

import { useEffect, useState } from 'react';
import { ApiError } from '@/lib/api';
import { toast } from '@/components/Toast';
import {
  getCurrentDocumentUrl,
  probeCurrentDocument,
  type CurrentDocumentType,
} from '@/lib/leaseDraftsClient';

export function CurrentDocumentDownload({
  groupId,
  documentType,
  label = 'Download document',
  variant = 'secondary',
  size = 'sm',
  unavailableNote = 'No document yet — nothing has been generated for this item.',
}: {
  /** The signature record's groupId (CRs: the zInspector document id). */
  groupId: string;
  /** Scope to one document type — a groupId can host several (a lease + its
   *  adoption addendum). Omit to serve the group's newest record overall. */
  documentType?: CurrentDocumentType;
  label?: string;
  /** Design-system button variant (`data-variant`). */
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  /** Copy shown when no document exists yet for this item. */
  unavailableNote?: string;
}): React.JSX.Element {
  const [availability, setAvailability] = useState<
    'probing' | 'available' | 'unavailable'
  >('probing');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setAvailability('probing');
    void (async () => {
      try {
        const res = await probeCurrentDocument(groupId, documentType);
        if (!live) return;
        setAvailability(res.available ? 'available' : 'unavailable');
      } catch {
        // Probe failure (auth blip / 5xx): keep the button — a real failure
        // surfaces on click, where the user can retry.
        if (live) setAvailability('available');
      }
    })();
    return () => {
      live = false;
    };
  }, [groupId, documentType]);

  if (availability === 'unavailable') {
    return (
      <span
        style={{
          color: 'var(--fg-subtle)',
          fontSize: 'var(--text-sm)',
          alignSelf: 'center',
        }}
      >
        {unavailableNote}
      </span>
    );
  }

  const onClick = async () => {
    setBusy(true);
    try {
      const { url } = await getCurrentDocumentUrl(groupId, documentType);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setAvailability('unavailable');
      } else {
        toast.error("Couldn't prepare the document download. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="btn"
      data-variant={variant}
      {...(size === 'sm' ? { 'data-size': 'sm' } : {})}
      disabled={busy || availability === 'probing'}
      onClick={() => void onClick()}
      title="Downloads the document in its current state — including any signatures collected so far."
    >
      {busy ? 'Preparing…' : label}
    </button>
  );
}
